/**
 * AutoCount - Add Part Page
 *
 * Progressive-complexity part builder. The required path is deliberately tiny:
 * a name and a quantity on hand, and nothing else. Every other field lives in
 * an optional section that is collapsed until the user taps "+ add" for it.
 * Simple by default, powerful on demand.
 */

import { useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { createPart, ApiError, PartCreateRequest } from "api";

// Category labels shown to the user mapped to the backend enum keys.
const CATEGORY_OPTIONS: { label: string; value: string }[] = [
  { label: "Engine", value: "ENGINE" },
  { label: "Transmission", value: "TRANSMISSION" },
  { label: "Brakes", value: "BRAKES" },
  { label: "Suspension", value: "SUSPENSION" },
  { label: "Steering", value: "STEERING" },
  { label: "Electrical", value: "ELECTRICAL" },
  { label: "Cooling", value: "COOLING" },
  { label: "Fuel System", value: "FUEL_SYSTEM" },
  { label: "Exhaust", value: "EXHAUST" },
  { label: "Lighting", value: "LIGHTING" },
  { label: "Wheels & Tires", value: "WHEELS_TIRES" },
  { label: "HVAC", value: "HVAC" },
  { label: "Drivetrain", value: "DRIVETRAIN" },
  { label: "Filters", value: "FILTERS" },
  { label: "Fluids", value: "FLUIDS" },
  { label: "Accessories", value: "ACCESSORIES" },
];

const UOM_OPTIONS = ["each", "box", "case", "pair", "set", "kit", "liter"];

// The optional sections, in the order they appear as "+ add" chips.
type SectionKey =
  | "sku"
  | "category"
  | "supplier"
  | "reorder"
  | "location"
  | "pricing"
  | "uom"
  | "photo";

const SECTIONS: { key: SectionKey; label: string; icon: string }[] = [
  { key: "sku", label: "SKU / part no. / barcode", icon: "qr_code_2" },
  { key: "category", label: "Category", icon: "category" },
  { key: "supplier", label: "Supplier / vendor", icon: "local_shipping" },
  { key: "reorder", label: "Reorder point", icon: "notifications_active" },
  { key: "location", label: "Location / bin", icon: "place" },
  { key: "pricing", label: "Cost & sell price", icon: "payments" },
  { key: "uom", label: "Unit of measure", icon: "straighten" },
  { key: "photo", label: "Photo", icon: "photo_camera" },
];

const MAX_PHOTO_BYTES = 1_000_000; // ~1MB source image

interface FormState {
  name: string;
  quantityOnHand: string;
  partNumber: string;
  oemPartNumber: string;
  categoryName: string | null;
  supplierName: string;
  supplierLeadTimeDays: string;
  reorderPoint: string;
  location: string;
  costPrice: string;
  retailPrice: string;
  unitOfMeasure: string | null;
  imageUrl: string; // data URL
}

const EMPTY_FORM: FormState = {
  name: "",
  quantityOnHand: "",
  partNumber: "",
  oemPartNumber: "",
  categoryName: null,
  supplierName: "",
  supplierLeadTimeDays: "",
  reorderPoint: "",
  location: "",
  costPrice: "",
  retailPrice: "",
  unitOfMeasure: null,
  imageUrl: "",
};

function PartsAdd(): JSX.Element {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [open, setOpen] = useState<Set<SectionKey>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const set = (field: keyof FormState) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const isOpen = (key: SectionKey) => open.has(key);

  const openSection = (key: SectionKey) =>
    setOpen((prev) => new Set(prev).add(key));

  const closeSection = (key: SectionKey) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Live margin preview when both cost and price are present.
  const margin = useMemo(() => {
    const cost = parseFloat(form.costPrice);
    const price = parseFloat(form.retailPrice);
    if (!Number.isFinite(cost) || !Number.isFinite(price) || price <= 0) {
      return null;
    }
    const dollars = price - cost;
    const pct = Math.round((dollars / price) * 100);
    return { dollars, pct };
  }, [form.costPrice, form.retailPrice]);

  const handlePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhotoError(null);
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
      setPhotoError("Please choose a PNG, JPG, WEBP or GIF image.");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError("Image is too large (max ~1MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setForm((prev) => ({ ...prev, imageUrl: String(reader.result || "") }));
    reader.onerror = () => setPhotoError("Could not read that image.");
    reader.readAsDataURL(file);
  };

  const canSave = form.name.trim().length > 0 && !submitting;

  const buildPayload = (): PartCreateRequest => {
    const payload: PartCreateRequest = { name: form.name.trim() };
    const qty = parseInt(form.quantityOnHand, 10);
    if (Number.isFinite(qty)) payload.quantityOnHand = qty;

    if (isOpen("sku")) {
      if (form.partNumber.trim()) payload.partNumber = form.partNumber.trim();
      if (form.oemPartNumber.trim())
        payload.oemPartNumber = form.oemPartNumber.trim();
    }
    if (isOpen("category") && form.categoryName)
      payload.categoryName = form.categoryName;
    if (isOpen("supplier")) {
      if (form.supplierName.trim())
        payload.supplierName = form.supplierName.trim();
      const lead = parseInt(form.supplierLeadTimeDays, 10);
      if (Number.isFinite(lead)) payload.supplierLeadTimeDays = lead;
    }
    if (isOpen("reorder")) {
      const rp = parseInt(form.reorderPoint, 10);
      if (Number.isFinite(rp)) payload.reorderPoint = rp;
    }
    if (isOpen("location") && form.location.trim()) {
      payload.location = form.location.trim();
      payload.binLocation = form.location.trim();
    }
    if (isOpen("pricing")) {
      const cost = parseFloat(form.costPrice);
      const price = parseFloat(form.retailPrice);
      if (Number.isFinite(cost)) payload.costPrice = cost;
      if (Number.isFinite(price)) payload.retailPrice = price;
    }
    if (isOpen("uom") && form.unitOfMeasure)
      payload.unitOfMeasure = form.unitOfMeasure;
    if (isOpen("photo") && form.imageUrl) payload.imageUrl = form.imageUrl;
    return payload;
  };

  const handleSubmit = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError(null);
    try {
      await createPart(buildPayload());
      navigate("/parts");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not save the part. Please try again."
      );
      setSubmitting(false);
    }
  };

  const sectionCard = (key: SectionKey, children: React.ReactNode) => (
    <Collapse in={isOpen(key)} timeout="auto" unmountOnExit key={key}>
      <MDBox
        mt={2}
        p={2}
        borderRadius="lg"
        sx={{ border: "1px solid rgba(255,255,255,0.10)" }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <MDTypography variant="button" fontWeight="bold">
            {SECTIONS.find((s) => s.key === key)?.label}
          </MDTypography>
          <IconButton
            size="small"
            aria-label={`remove ${key}`}
            onClick={() => closeSection(key)}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
        {children}
      </MDBox>
    </Collapse>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={9}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Add New Part
                </MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                  Just a name and a quantity to start — add more only if you
                  want.
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                {/* ---- Required path: name + quantity ---- */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={8}>
                    <MDInput
                      label="Part name"
                      placeholder="e.g. Front Brake Pads"
                      fullWidth
                      required
                      value={form.name}
                      onChange={set("name")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <MDInput
                      label="Quantity on hand"
                      type="number"
                      fullWidth
                      value={form.quantityOnHand}
                      onChange={set("quantityOnHand")}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* ---- Progressive "+ add" chips ---- */}
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Add more details (optional)
                </MDTypography>
                <MDBox mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                  {SECTIONS.filter((s) => !isOpen(s.key)).map((s) => (
                    <MDButton
                      key={s.key}
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => openSection(s.key)}
                    >
                      <Icon sx={{ mr: 0.5 }}>add</Icon>
                      {s.label}
                    </MDButton>
                  ))}
                  {open.size === SECTIONS.length && (
                    <MDTypography variant="caption" color="text">
                      All optional sections added.
                    </MDTypography>
                  )}
                </MDBox>

                {/* ---- Optional sections ---- */}
                {sectionCard(
                  "sku",
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="SKU / part number"
                        placeholder="Auto-generated if left blank"
                        fullWidth
                        value={form.partNumber}
                        onChange={set("partNumber")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="OEM / barcode number"
                        fullWidth
                        value={form.oemPartNumber}
                        onChange={set("oemPartNumber")}
                      />
                    </Grid>
                  </Grid>
                )}

                {sectionCard(
                  "category",
                  <Autocomplete
                    options={CATEGORY_OPTIONS}
                    value={
                      CATEGORY_OPTIONS.find(
                        (o) => o.value === form.categoryName
                      ) || null
                    }
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    onChange={(_, v) =>
                      setForm((prev) => ({
                        ...prev,
                        categoryName: v ? v.value : null,
                      }))
                    }
                    renderInput={(params) => (
                      <MDInput {...params} label="Category" />
                    )}
                  />
                )}

                {sectionCard(
                  "supplier",
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                      <MDInput
                        label="Supplier / vendor name"
                        fullWidth
                        value={form.supplierName}
                        onChange={set("supplierName")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <MDInput
                        label="Lead time (days)"
                        type="number"
                        fullWidth
                        value={form.supplierLeadTimeDays}
                        onChange={set("supplierLeadTimeDays")}
                      />
                    </Grid>
                  </Grid>
                )}

                {sectionCard(
                  "reorder",
                  <MDBox>
                    <MDInput
                      label="Reorder point (low-stock threshold)"
                      type="number"
                      fullWidth
                      value={form.reorderPoint}
                      onChange={set("reorderPoint")}
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      mt={0.5}
                      display="block"
                    >
                      We&apos;ll flag this part on the dashboard when on-hand
                      stock drops to this level.
                    </MDTypography>
                  </MDBox>
                )}

                {sectionCard(
                  "location",
                  <MDInput
                    label="Location / bin (e.g. A1-03)"
                    fullWidth
                    value={form.location}
                    onChange={set("location")}
                  />
                )}

                {sectionCard(
                  "pricing",
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={4}>
                      <MDInput
                        label="Cost ($)"
                        type="number"
                        fullWidth
                        value={form.costPrice}
                        onChange={set("costPrice")}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <MDInput
                        label="Sell price ($)"
                        type="number"
                        fullWidth
                        value={form.retailPrice}
                        onChange={set("retailPrice")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {margin ? (
                        <MDBox textAlign={{ xs: "left", sm: "center" }}>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="bold"
                            display="block"
                          >
                            Margin
                          </MDTypography>
                          <MDTypography
                            variant="h6"
                            color={margin.dollars >= 0 ? "success" : "error"}
                          >
                            ${margin.dollars.toFixed(2)} ({margin.pct}%)
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <MDTypography variant="caption" color="text">
                          Margin shows once cost and price are set.
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>
                )}

                {sectionCard(
                  "uom",
                  <Autocomplete
                    options={UOM_OPTIONS}
                    value={form.unitOfMeasure}
                    onChange={(_, v) =>
                      setForm((prev) => ({ ...prev, unitOfMeasure: v }))
                    }
                    renderInput={(params) => (
                      <MDInput {...params} label="Unit of measure" />
                    )}
                  />
                )}

                {sectionCard(
                  "photo",
                  <MDBox>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Icon sx={{ mr: 0.5 }}>upload</Icon>
                      Choose photo
                    </MDButton>
                    <input
                      ref={fileInputRef}
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handlePhoto}
                    />
                    {photoError && (
                      <MDBox mt={1}>
                        <Alert severity="warning">{photoError}</Alert>
                      </MDBox>
                    )}
                    {form.imageUrl && (
                      <MDBox mt={2}>
                        <img
                          src={form.imageUrl}
                          alt="Part preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 180,
                            borderRadius: 8,
                          }}
                        />
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {error && (
                  <MDBox mt={3}>
                    <Alert severity="error">{error}</Alert>
                  </MDBox>
                )}

                <MDBox
                  mt={4}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  flexWrap="wrap"
                >
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/parts")}
                    disabled={submitting}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={!canSave}
                  >
                    {submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      "Save Part"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PartsAdd;
